// Key Dates: something coming up that the user needs to get ready for (flying home for Christmas, a
// birthday, a renewal). One Firestore document each: `title`, `date` ("YYYY-MM-DD"), `remindBefore` (a
// count and unit, "3m"), `repeat` ("weekly", "monthly", "yearly", or null for once; read through repeatOf, because
// dates saved before 29 Oct 2026 have only `yearly: true`; `yearly` is still written to match) and `goalId` (the goal it is for, or
// null). Its tasks are ordinary tasks with `keyDateId` set. From the day reminders start, its unfinished
// tasks show on Radar (INTENT.md, decisions log, 20 Oct 2026).
import { daysUntil, todayKey, toKey, parseKey } from "../../shared/dates";
import { monthDay, shortDate } from "../../shared/deadlines";
export { longDate, shortDate } from "../../shared/deadlines";

export const KEY_DATES_COLLECTION = "keyDates";

// How far ahead reminders start, stored as a count and a unit: "5d", "2w", "3m".
export const REMIND_UNITS = [
  { id: "d", one: "day", many: "days", max: 90 },
  { id: "w", one: "week", many: "weeks", max: 52 },
  { id: "m", one: "month", many: "months", max: 24 },
];
export const DEFAULT_REMIND = "1m";
export const remindUnit = (id) => REMIND_UNITS.find(u => u.id === id);

// How often a Key Date comes back (Craig, 29 Oct 2026: "expand the 'recurring key date' functionality beyond just
// 'every year'"). `units` caps "Start reminding me" so reminders start after the last time has passed (a weekly date
// reminded a month ahead would never leave Radar); A date that happens once keeps REMIND_UNITS. `remind` is what a reminder that no longer
// fits becomes, and `capWords` says why + stops at the limit.
export const REPEATS = [
  { id: "weekly",  label: "Weekly",  words: "Every week",  next: "the same day next week",  units: { d: 6 },        remind: "1d",
    capWords: "A weekly date can remind you up to 6 days ahead, so the reminders end before the next week starts." },
  { id: "monthly", label: "Monthly", words: "Every month", next: "the same day next month", units: { d: 27, w: 3 }, remind: "1w",
    capWords: "A monthly date can remind you up to 3 weeks ahead, so the reminders end before the next month starts." },
  { id: "yearly",  label: "Yearly",  words: "Every year",  next: "the same day next year",  units: { d: 90, w: 51, m: 11 }, remind: DEFAULT_REMIND,
    capWords: "A yearly date can remind you up to 11 months ahead, so the reminders end before the next year starts." },
];
// The REPEATS entry a Key Date follows, or null when it happens once.
export const repeatOf = (keyDate) => {
  const id = keyDate?.repeat !== undefined ? keyDate.repeat : keyDate?.yearly ? "yearly" : null;
  return REPEATS.find(r => r.id === id) || null;
};
// The units "Start reminding me" offers for a repeat id (or null, once), each with its maximum.
export const remindUnitsFor = (repeatId) => {
  const caps = REPEATS.find(r => r.id === repeatId)?.units;
  return caps ? REMIND_UNITS.filter(u => caps[u.id]).map(u => ({ ...u, max: caps[u.id] })) : REMIND_UNITS;
};
// The reminder kept when the repeat changes: as it was if it still fits, otherwise that repeat's own.
export const fitRemind = (remindBefore, repeatId) => {
  const { n, unit } = parseRemind(remindBefore);
  const u = remindUnitsFor(repeatId).find(x => x.id === unit);
  return u && n <= u.max ? remindBefore : REPEATS.find(r => r.id === repeatId)?.remind || DEFAULT_REMIND;
};

// { n, unit } from a stored value; anything unreadable is the default, 1 month.
export const parseRemind = (remindBefore) => {
  const m = /^(\d+)([dwm])$/.exec(remindBefore || "");
  const n = m ? Number(m[1]) : 0;
  return n >= 1 ? { n, unit: m[2] } : parseRemind(DEFAULT_REMIND);
};
export const formatRemind = (n, unit) => `${n}${unit}`;

// "1 month before", "10 days before".
export const remindLabel = (remindBefore) => {
  const { n, unit } = parseRemind(remindBefore);
  const u = remindUnit(unit);
  return `${n} ${n === 1 ? u.one : u.many} before`;
};

// The day reminders start. A month back from the 31st lands on the last day of the shorter month.
export const remindStart = (dateStr, remindBefore = DEFAULT_REMIND) => {
  const { n, unit } = parseRemind(remindBefore);
  const d = parseKey(dateStr);
  if (unit !== "m") { d.setDate(d.getDate() - n * (unit === "w" ? 7 : 1)); return toKey(d); }
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() - n);
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, lastDay));
  return toKey(d);
};

// "Today", "Tomorrow", "In 25 days"; for a past date, "Yesterday" or "3 days ago".
export const countdown = (dateStr) => {
  const n = daysUntil(dateStr);
  if (n === 0) return "Today";
  if (n === 1) return "Tomorrow";
  if (n === -1) return "Yesterday";
  return n > 0 ? `In ${n} days` : `${-n} days ago`;
};

const byDate = (a, b) => a.date.localeCompare(b.date) || (a.title || "").localeCompare(b.title || "");

// Dates by where they stand. "now": reminders have started and the date hasn't passed (Radar shows
// these). "later": reminders haven't started. "coming": both, in date order: the page lists them as one, so
// a date always sits above a later one (Craig, 21 Oct 2026). "past": most recent first.
export const groupKeyDates = (keyDates, today = todayKey()) => {
  const dated = (keyDates || []).filter(k => k.date);
  return {
    now: dated.filter(k => k.date >= today && remindStart(k.date, k.remindBefore) <= today).sort(byDate),
    later: dated.filter(k => remindStart(k.date, k.remindBefore) > today).sort(byDate),
    past: dated.filter(k => k.date < today).sort((a, b) => byDate(b, a)),
    coming: dated.filter(k => k.date >= today).sort(byDate),
  };
};

// A Key Date's tasks: unfinished first, then in the order they were added.
export const tasksForKeyDate = (tasks, keyDateId) =>
  (tasks || []).filter(t => t.keyDateId === keyDateId)
    .sort((a, b) => Number(!!a.done) - Number(!!b.done) || (a.createdAt?.toMillis?.() ?? 0) - (b.createdAt?.toMillis?.() ?? 0));

// Radar's "Key Dates Coming Up": the unfinished tasks of every date in its reminder period, soonest
// date first. `shownElsewhere` holds the ids of tasks already in Overdue or Due soon: a task shows once,
// in the group that says it's due, with its Key Date named there.
export const keyDateRadarItems = (keyDates, tasks, today = todayKey(), shownElsewhere = new Set()) =>
  groupKeyDates(keyDates, today).now.flatMap(keyDate =>
    tasksForKeyDate(tasks, keyDate.id).filter(t => !t.done && !shownElsewhere.has(t.id)).map(task => ({ task, keyDate })));

// Radar's "Key Dates Coming Up": every date whose reminders have started, soonest first, as cards (Craig, 24 Oct 2026),
// whatever its tasks. Craig: "I don't want to drop off dates that have no open tasks. The fact that it doesn't
// currently have tasks doesn't necessarily mean it shouldn't be in your face."
export const keyDatesToGetReadyFor = (keyDates, today = todayKey()) => groupKeyDates(keyDates, today).now;

// A new task written for a Key Date: on that date, and in its goal when the goal is active (shown and changeable in
// the task editor).
export const newTaskForKeyDate = (keyDate, goals) => ({ keyDateId: keyDate.id, parentGoalId: goalForNewTasks(keyDate.goalId, goals)?.id || null });

// The date a repeating Key Date will have once it moves on (today or later); any other date as it is.
export const upcomingDate = (keyDate, today = todayKey()) => repeatRollover(keyDate, [], today)?.date || keyDate.date;

// " · Every week", " · Every month" or " · Every year" after a repeating date's words; nothing for one that happens once.
export const repeatWords = (keyDate) => { const r = repeatOf(keyDate); return r ? ` · ${r.words}` : ""; };

// The goal a Key Date's new tasks start in: its goal while that goal is active. A parked or finished goal
// stays on the date but new tasks don't go in it. The Key Date editor and the card's "+ Add a task" agree.
export const goalForNewTasks = (goalId, goals) => (goals || []).find(g => g.id === goalId && g.status === "active") || null;

// The Key Dates that are for a goal, soonest first.
export const keyDatesForGoal = (keyDates, goalId) =>
  (keyDates || []).filter(k => k.date && k.goalId === goalId).sort(byDate);

// The same day `months` months on. A day the shorter month doesn't have becomes its last day (January 31 → February 28).
// `day` is the day of the month to aim for, when it isn't the date's own: a date the user put on the 31st that has been
// moved to February 28 goes back to the 31st in March.
const shiftMonths = (dateStr, months, day = parseKey(dateStr).getDate()) => {
  const d = parseKey(dateStr);
  d.setDate(1);
  d.setMonth(d.getMonth() + months);
  d.setDate(Math.min(day, new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()));
  return toKey(d);
};

// The same day `years` years on. February 29 becomes February 28 in a year without one.
export const shiftYears = (dateStr, years) => shiftMonths(dateStr, years * 12);

// A date moved on `steps` repeats: weeks, months or years. `day` is the Key Date's own day of the month (see dayOf).
export const shiftByRepeat = (dateStr, repeatId, steps, day) => {
  if (repeatId === "monthly") return shiftMonths(dateStr, steps, day);
  if (repeatId === "yearly") return shiftMonths(dateStr, steps * 12, day);
  const d = parseKey(dateStr);
  d.setDate(d.getDate() + 7 * steps);
  return toKey(d);
};

// The day of the month a Key Date was set on: `day`, written whenever its date is saved, so moving a date on to a
// shorter month's last day doesn't move it there for good. Dates saved before it use their date's day.
export const dayOf = (keyDate) => keyDate.day || parseKey(keyDate.date).getDate();

// A repeating Key Date whose day has passed moves to its next one, today or later. Its tasks come back for the next
// time: done ones are to do again (and off Today), and deadlines up to the old date move on by the same number of
// weeks, months or years. A deadline already set past the old date was set for the coming one and stays.
// Returns null when there is nothing to move. { date, steps, taskChanges: [{ id, fields }] }.
export const repeatRollover = (keyDate, tasks, today = todayKey()) => {
  const repeat = repeatOf(keyDate);
  if (!repeat || !keyDate.date || keyDate.date >= today) return null;
  const day = dayOf(keyDate);
  let steps = 1;
  while (shiftByRepeat(keyDate.date, repeat.id, steps, day) < today) steps++;
  const taskChanges = tasksForKeyDate(tasks, keyDate.id).map(t => {
    const fields = {};
    if (t.done) Object.assign(fields, { done: false, todayPhase: null, todayTime: null });
    if (t.deadline && t.deadline <= keyDate.date) fields.deadline = shiftByRepeat(t.deadline, repeat.id, steps);
    return { id: t.id, fields };
  }).filter(c => Object.keys(c.fields).length);
  return { date: shiftByRepeat(keyDate.date, repeat.id, steps, day), steps, taskChanges };
};

// The task editor's "Key date" choices: upcoming dates, soonest first, as "Fly home for Christmas · Dec 19".
// A past date stays in the list only when the task is already tied to it, so opening it never drops the link.
// A link to a date that no longer exists offers nothing, so the editor treats it as no key date.
export const keyDateChoices = (keyDates, currentId = null, today = todayKey()) =>
  (keyDates || []).filter(k => k.date && (k.date >= today || k.id === currentId))
    .sort(byDate)
    .map(k => ({ id: k.id, label: `${k.title} · ${monthDay(k.date)}`, keyDate: k }));

// Under the task editor's "Key date": where the task will show, naming the date and when it reaches Radar.
export const keyDateTaskHint = (keyDate, today = todayKey()) => {
  const start = remindStart(keyDate.date, keyDate.remindBefore);
  const radar = keyDate.date < today ? "The date has passed, so it won't show on Radar."
    : start <= today ? "It's on Radar now." : `It shows on Radar from ${shortDate(start)}.`;
  return `This task shows on the ${keyDate.title} card. ${radar}`;
};

// An edit leaves out a date the user didn't change: a repeating date may have moved on while the editor was
// open, and writing the old one back would move it, and reopen its tasks, a second time.
export const unchangedDateLeftOut = (fields, opened) => {
  if (fields.date !== opened.date) return fields;
  const { date, ...rest } = fields;
  return rest;
};
